import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from './msalConfig';
import { microsoftSsoConfig } from './ssoConfig';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const shouldBypassAuth = process.env.REACT_APP_BYPASS_AUTH === 'true';
  const msalAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  // Only used for bypass mode (so we can still "logout" in local dev).
  const [bypassAuthenticated, setBypassAuthenticated] = useState(shouldBypassAuth);

  // Check accounts directly from instance as fallback (useIsAuthenticated hook can lag after redirect).
  const hasAccounts = instance && instance.getAllAccounts().length > 0;
  const isAuthenticated = shouldBypassAuth ? bypassAuthenticated : (msalAuthenticated || hasAccounts);

  const loginWithSso = useCallback(async () => {
    if (shouldBypassAuth) {
      setBypassAuthenticated(true);
      return;
    }
    await instance.loginRedirect(loginRequest);
  }, [instance, shouldBypassAuth]);

  const logout = useCallback(async () => {
    if (shouldBypassAuth) {
      setBypassAuthenticated(false);
      return;
    }
    await instance.logoutRedirect();
  }, [instance, shouldBypassAuth]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      shouldBypassAuth,
      loginWithSso,
      logout,
      microsoftSsoConfig,
    }),
    [isAuthenticated, loginWithSso, logout, shouldBypassAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

